import { FormEvent, useState } from 'react'
import jwt from 'jsonwebtoken'
import { APP_BASE_URL, HttpClient } from '../../../utils/utils'
import ApiUtils from '../../../utils/api.utils'
import { toast } from 'react-toastify'
import { useTextField, useUserLocation } from '../../../utils/hooks.util'
import { useRouter } from 'next/router'
import { Routes } from '../../../utils/Routes.utils'

const googleSignInStatePayload = {
  method: 'google',
}

export function googleSignInIntent() {
  const oauth_scopes =
    'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile openid'
  const redirect_uri = APP_BASE_URL + '/oauth'
  const client_id =
    '783213191359-9brctkr2iqa5s0br3smn0q7clqfbe775.apps.googleusercontent.com'
  const state = jwt.sign(
    googleSignInStatePayload,
    process.env.NEXT_PUBLIC_OAUTH_STATE_SECRET || ''
  )
  const oauthRedirectURL = `https://accounts.google.com/o/oauth2/v2/auth?scope=${oauth_scopes}&include_granted_scopes=true&response_type=token&redirect_uri=${redirect_uri}&client_id=${client_id}&state=${state}`
  window.location.href = oauthRedirectURL
}

const facebookSignInStatePayload = {
  method: 'facebook',
}

export function facebookSignInIntent() {
  const app_id = '272241635038611'
  const redirect_uri = APP_BASE_URL + '/oauth'
  const scopes = 'email public_profile'
  const response_type = 'token'
  const state = jwt.sign(
    facebookSignInStatePayload,
    process.env.NEXT_PUBLIC_OAUTH_STATE_SECRET || ''
  )
  const oauthRedirectURL = `https://www.facebook.com/v13.0/dialog/oauth?client_id=${app_id}&redirect_uri=${redirect_uri}&response_type=${response_type}&scope=${scopes}&state=${state}`
  window.location.href = oauthRedirectURL
}

const appleSignInStatePayload = {
  method: 'apple',
}

export function appleSignInIntent() {
  const client_id = 'com.raptures.app'
  const redirect_uri = APP_BASE_URL + '/oauth'
  const scopes = 'email name'
  const response_type = 'form_post'
  const state = jwt.sign(
    appleSignInStatePayload,
    process.env.NEXT_PUBLIC_OAUTH_STATE_SECRET || ''
  )
  const oauthRedirectURL = `https://appleid.apple.com/auth/authorize?client_id=${client_id}&redirect_uri=${redirect_uri}&response_type=code&state=${state}&scope=${scopes}&response_mode=${response_type}`
  window.location.href = oauthRedirectURL
}

type LoginFormData = {
  email: string
  password: string
}

async function loginUser(body: LoginFormData) {
  return HttpClient({
    path: ApiUtils.Endpoints.login.mirror,
    method: 'post',
    body,
  })
}

type useLoginFormParams = {
  skipRedirects?: boolean
  onLogin?: VoidFunction
}

export function useLoginForm(params?: useLoginFormParams) {
  const email = useTextField()
  const password = useTextField()
  const [loading, setLoading] = useState(false)
  const router = useRouter()
  const { country } = useUserLocation()

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setLoading(true)
    const { result, error } = await loginUser({
      email: email.value,
      password: password.value,
    })
    setLoading(false)
    if (error) {
      toast.error(error.message)
      return
    }
    toast.success(result?.message)

    if (params?.onLogin) {
      params.onLogin()
    }

    if (!params?.skipRedirects) {
      const url = new URL(window.location.href).searchParams
      ;(global as any).analytics.track('User Login', {
        email: email.value,
        source: url.get('utm_source'),
        medium: url.get('utm_medium'),
        campaign: url.get('utm_campaign'),
        content: url.get('utm_content'),
        device: window.navigator.userAgent,
        location: country,
      })
      if (router.asPath === '/') {
        await router.push(Routes.ORIGINS)
        return
      }
      await router.push(router.query.redirect?.toString() || Routes.PROFILE)
    }
  }

  async function handleSkip() {
    if (router.asPath === '/') {
      await router.push(Routes.ORIGINS)
      return
    }
    if (router.query.skip) {
      await router.push(router.query.skip?.toString())
      return
    }
    await router.back()
  }

  return {
    email,
    password,
    googleSignInIntent,
    appleSignInIntent,
    facebookSignInIntent,
    onSubmit: handleSubmit,
    onSkip: handleSkip,
    loading,
  }
}
